/**
 * Extra daycare-specific hooks using TanStack Query
 * Provides data fetching, caching, and mutations for extra daycare operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { SpecialAllocationsService } from '@/api/services/SpecialAllocationsService';
import type {
  ExtraDaycareResponse,
  ExtraDaycareListResponse,
  CreateExtraDaycareRequest,
  UpdateExtraDaycareRequest,
} from '@/types/capacity';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { calendarKeys } from './useCalendarWeek';
import { invalidateMoneyViews } from '@/hooks/reconciliation/invalidateMoneyViews';

// Query keys for extra daycare
export const extraDaycareKeys = {
  all: ['extra-daycare'] as const,
  lists: () => [...extraDaycareKeys.all, 'list'] as const,
  list: (filters: Record<string, string | number | undefined>) => [...extraDaycareKeys.lists(), { filters }] as const,
  details: () => [...extraDaycareKeys.all, 'detail'] as const,
  detail: (id: string) => [...extraDaycareKeys.details(), id] as const,
};

// Hook to fetch all extra daycare entries
export function useExtraDaycares(
  childId?: string,
  groupId?: string,
  date?: string,
  limit = 50,
  offset = 0
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: extraDaycareKeys.list({ childId, groupId, date, limit, offset }),
    queryFn: async () => {
      const response = await SpecialAllocationsService.getApiV1ExtraDaycare(
        limit,
        offset,
        childId,
        groupId,
        date
      );
      return response as ExtraDaycareListResponse;
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch a single extra daycare entry
export function useExtraDaycare(id: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: extraDaycareKeys.detail(id),
    queryFn: async () => {
      const response = await SpecialAllocationsService.getApiV1ExtraDaycare1(id);
      return response as ExtraDaycareResponse;
    },
    enabled: !!id && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to create a new extra daycare entry
export function useCreateExtraDaycare() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (extraDaycare: CreateExtraDaycareRequest) => {
      const response = await SpecialAllocationsService.postApiV1ExtraDaycare(extraDaycare);
      return response as ExtraDaycareResponse;
    },
    onSuccess: async (data) => {
      // Set the created extra daycare data in the detail cache
      if (data?.id) {
        queryClient.setQueryData(extraDaycareKeys.detail(data.id), data);
      }

      // Invalidate extra daycare lists
      await queryClient.invalidateQueries({
        queryKey: extraDaycareKeys.lists(),
        refetchType: 'active',
      });

      // An extra daycare day is a billing INPUT: it lands on the next invoice
      // and on the debtor's Outstanding. The shared pass also broadcasts to the
      // operator's other browser tabs, which a local invalidate never does.
      await invalidateMoneyViews(queryClient);

      // Invalidate capacity queries to refresh sync status and categorization
      await queryClient.invalidateQueries({
        queryKey: ['capacity'],
        refetchType: 'active',
      });

      // Invalidate calendar queries to update calendar views
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.all,
        refetchType: 'active',
      });

      toast.success(i18n._({
        id: "capacity.extraDaycare.toast.createSuccess",
        message: "Extra daycare created successfully",
      }));
    },
    onError: (error: Error) => {
      const errorMessage = error.message || "Unknown error";
      const lowerMessage = errorMessage.toLowerCase();

      // Check for duplicate entry
      if (lowerMessage.includes("duplicate") || lowerMessage.includes("already exists")) {
        toast.error(i18n._({
          id: "common.error.duplicateEntry",
          message: "This entry already exists",
        }));
        return;
      }

      // Handle validation errors
      if (lowerMessage.includes("validation") || lowerMessage.includes("invalid")) {
        toast.error(i18n._({
          id: "common.error.validationFailed",
          message: "Validation failed. Please check your input.",
        }));
        return;
      }

      // Generic error fallback
      toast.error(i18n._({
        id: "capacity.extraDaycare.toast.createFailed",
        message: "Failed to create extra daycare",
      }));
    },
  });
}

// Hook to update an extra daycare entry
export function useUpdateExtraDaycare() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateExtraDaycareRequest }) => {
      const response = await SpecialAllocationsService.putApiV1ExtraDaycare(id, data);
      return response as ExtraDaycareResponse;
    },
    onSuccess: async (data, variables) => {
      // Update the detail cache immediately with the fresh data from the server
      queryClient.setQueryData(extraDaycareKeys.detail(variables.id), data);

      // Update any list queries that contain this extra daycare optimistically
      queryClient.setQueriesData<ExtraDaycareListResponse>(
        { queryKey: extraDaycareKeys.lists() },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            items: oldData.items?.map((extraDaycare) =>
              extraDaycare.id === variables.id ? data : extraDaycare
            ) || [],
          };
        }
      );

      // Invalidate extra daycare lists and WAIT for refetch
      await queryClient.invalidateQueries({
        queryKey: extraDaycareKeys.lists(),
        refetchType: 'active',
      });

      // Billing input — see the create hook above.
      await invalidateMoneyViews(queryClient);

      // Invalidate capacity queries to refresh sync status and categorization
      await queryClient.invalidateQueries({
        queryKey: ['capacity'],
        refetchType: 'active',
      });

      // Invalidate calendar queries to update calendar views
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.all,
        refetchType: 'active',
      });

      toast.success(i18n._({
        id: "capacity.extraDaycare.toast.updateSuccess",
        message: "Extra daycare updated successfully",
      }));
    },
    onError: (error: Error) => {
      const errorMessage = error.message || "Unknown error";
      const lowerMessage = errorMessage.toLowerCase();

      // Handle optimistic locking conflicts (409)
      if (lowerMessage.includes("409") || lowerMessage.includes("concurrent modification")) {
        toast.error(i18n._({
          id: "capacity.extraDaycare.error.concurrentModification",
          message: "This extra daycare was modified by someone else. Please refresh and try again.",
        }));
        return;
      }

      // Handle validation errors
      if (lowerMessage.includes("validation") || lowerMessage.includes("invalid")) {
        toast.error(i18n._({
          id: "common.error.validationFailed",
          message: "Validation failed. Please check your input.",
        }));
        return;
      }

      // Generic error fallback
      toast.error(i18n._({
        id: "capacity.extraDaycare.toast.updateFailed",
        message: "Failed to update extra daycare",
      }));
    },
  });
}

// Hook to delete an extra daycare entry
export function useDeleteExtraDaycare() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await SpecialAllocationsService.deleteApiV1ExtraDaycare(id);
    },
    onSuccess: async (_, variables) => {
      // Invalidate extra daycare lists
      await queryClient.invalidateQueries({
        queryKey: extraDaycareKeys.lists(),
        refetchType: 'active',
      });

      // An extra daycare day is a billing INPUT: it lands on the next invoice
      // and on the debtor's Outstanding. The shared pass also broadcasts to the
      // operator's other browser tabs, which a local invalidate never does.
      await invalidateMoneyViews(queryClient);

      // Remove the deleted extra daycare from detail cache
      queryClient.removeQueries({ queryKey: extraDaycareKeys.detail(variables.id) });

      // Invalidate capacity queries to refresh sync status and categorization
      await queryClient.invalidateQueries({
        queryKey: ['capacity'],
        refetchType: 'active',
      });

      // Invalidate calendar queries to update calendar views
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.all,
        refetchType: 'active',
      });

      toast.success(i18n._({
        id: "capacity.extraDaycare.toast.deleteSuccess",
        message: "Extra daycare deleted successfully",
      }));
    },
    onError: () => {
      toast.error(i18n._({
        id: "capacity.extraDaycare.toast.deleteFailed",
        message: "Failed to delete extra daycare",
      }));
    },
  });
}
